import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Search, Filter, Grid, List } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { useFavorites } from '../context/FavoritesContext';
import { GET_TOOLS_BY_IDS } from '../graphql/queries/getToolsByIds';
import Button from '../components/ui/Button';
import ToolCard from '../components/features/ToolCard';
import { Tool } from '../types';

const Favorites: React.FC = () => {  const navigate = useNavigate();  const { favorites: favoriteIds, removeFromFavorites } = useFavorites();
  const [favorites, setFavorites] = useState<Tool[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Get favorite tool IDs from context
  const favoriteToolIds = favoriteIds.map(tool => tool.id);

  // Fetch fresh data for favorites
  const { data: toolsData, loading, error } = useQuery(GET_TOOLS_BY_IDS, {
    variables: {
      ids: favoriteToolIds,
      date: new Date().toISOString().split('T')[0]
    },
    skip: favoriteToolIds.length === 0,
    fetchPolicy: 'cache-and-network'
  });

  // Log any errors
  useEffect(() => {
    if (error) {
      console.log('GET_TOOLS_BY_IDS error:', error);
      console.log('This query might not be supported by backend yet.');
    }
  }, [error]);

  // Update favorites when data is fetched
  useEffect(() => {
    if (toolsData?.toolsByIds) {
      setFavorites(toolsData.toolsByIds);
    } else if (favoriteToolIds.length === 0) {
      setFavorites([]);
    } else if (!loading && favoriteIds.length > 0) {
      // Fallback to cached favorites if query fails
      setFavorites(favoriteIds);
    }
  }, [toolsData, favoriteToolIds.length, favoriteIds, loading]);

  // Get unique categories from favorites
  const categories = Array.from(
    new Set(favorites.flatMap(tool => 
      tool.categories.map(cat => cat.name)
    ))
  ).sort();

  // Filter favorites based on search and category
  const filteredFavorites = favorites.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    const toolCategories = tool.categories.map(cat => cat.name);
    const matchesCategory = categoryFilter === 'all' || toolCategories.includes(categoryFilter);
    return matchesSearch && matchesCategory;
  });

  const handleRemoveFromFavorites = (toolId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    removeFromFavorites(toolId);
  };

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center text-green-600 mb-4 hover:text-green-700">
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Tilbage til dashboard</span>
        </button>
        <div className="flex items-center">
          <Heart className="h-8 w-8 text-red-500 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mine favoritter</h1>
            <p className="text-gray-600">Værktøj du har gemt til senere</p>
          </div>
        </div>
      </div>

      {loading && favoriteToolIds.length > 0 && favorites.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : favoriteToolIds.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Heart className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen favoritter endnu</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start med at gennemse værktøj og klik på hjerte ikonet for at gemme dine favoritter.
          </p>
          <div className="mt-6">
            <Link to="/browse">
              <Button variant="primary">Gennemse værktøj</Button>
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Search Bar */}
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Søg i favoritter..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>

              {/* Category Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
                >
                  <option value="all">Alle kategorier</option>
                  {categories.map((category, index) => (
                    <option key={`category-${index}`} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Toggle */}
              <div className="hidden sm:flex items-center space-x-2 border border-gray-300 rounded-md">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center justify-center h-9 w-9 rounded-l-md ${
                    viewMode === 'grid' ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <Grid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center justify-center h-9 w-9 rounded-r-md ${
                    viewMode === 'list' ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <List className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-gray-600">
              Viser <span className="font-medium">{filteredFavorites.length}</span> af{' '}
              <span className="font-medium">{favorites.length}</span> favoritter
            </div>
          </div>

          {/* No Results */}
          {filteredFavorites.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <Search className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen favoritter fundet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Prøv at justere dine søge- eller filterparametre.
              </p>
              <div className="mt-6">
                <Button onClick={() => { setSearchQuery(''); setCategoryFilter('all'); }} variant="outline">
                  Ryd filtre
                </Button>
              </div>
            </div>
          )}

          {/* Favorites Grid/List */}
          {filteredFavorites.length > 0 && (
            <div className={`
              ${viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' 
                : 'space-y-4'
              }
            `}>
              {filteredFavorites.map((tool) => (
                <div key={tool.id} className="relative group">
                  <ToolCard tool={tool} variant={viewMode} />

                  {/* Remove from Favorites Button */}
                  <button
                    onClick={(e) => handleRemoveFromFavorites(tool.id, e)}
                    className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-50"
                    title="Fjern fra favoritter"
                  >
                    <Heart className="h-5 w-5 text-red-500 fill-current" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          {favorites.length > 0 && (
            <div className="mt-12 bg-green-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Hurtige handlinger</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/browse">
                  <Button variant="primary" className="w-full sm:w-auto">
                    Gennemse mere værktøj
                  </Button>
                </Link>
                <Link to="/categories">
                  <Button variant="outline" className="w-full sm:w-auto">
                    Udforsk kategorier
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Favorites;